// frontend/src/hooks/useDashboard.ts - COMPLETE DASHBOARD HOOKS WITH ALL FUNCTIONALITY
// Entity-specific hooks for dashboard with infinite loop fixes and full feature set

import { useCallback, useMemo } from 'react';
import apiClient from '@/api';
import { useAuthStore } from '@/store/auth';
import { 
  useApi, 
  useMutation, 
  normalizeSingleResponse, 
  normalizePaginatedResponse,
  type UseApiOptions,
  type UseMutationOptions
} from './useApi';

// Import only types from API - not functions
import type {
  // Response types
  StandardResponse,
  PaginatedResponse,
  ImplementorDataResponse,
  
  // Dashboard types
  DashboardSummary,
  DashboardMetrics,
  DashboardQueryParams,
  DashboardActivity,
  DashboardTask,
  
  // User and organization types
  UserRole,
  Priority,
  
  // Query parameter types
  QueryParams
} from '@/api/types';

// ===== LOCAL INTERFACES =====

/**
 * Dashboard preferences interface
 */
interface DashboardPreferences {
  dashboardRefreshInterval: number; // seconds
  defaultView: 'grid' | 'list' | 'cards';
  showCompletedActions: boolean;
  actionFilters: boolean;
  theme: 'light' | 'dark' | 'auto';
  autoRefresh: boolean;
  widgetConfiguration?: {
    showMetrics: boolean;
    showActivity: boolean;
    showTasks: boolean;
    showNotifications: boolean;
    showStorage: boolean;
  };
  compactMode: boolean;
  timeFormat: '12h' | '24h';
  dateFormat: string;
}

/**
 * Dashboard preferences update request
 */
interface DashboardPreferencesUpdateRequest {
  dashboardRefreshInterval?: number;
  defaultView?: 'grid' | 'list' | 'cards';
  showCompletedActions?: boolean;
  actionFilters?: boolean;
  theme?: 'light' | 'dark' | 'auto';
  autoRefresh?: boolean;
  widgetConfiguration?: DashboardPreferences['widgetConfiguration'];
  compactMode?: boolean;
  timeFormat?: '12h' | '24h';
  dateFormat?: string;
}

/**
 * Enhanced dashboard summary with calculated fields
 */
interface EnhancedDashboardSummary extends DashboardSummary {
  calculatedMetrics?: {
    productivityScore: number;
    responseRate: number;
    averageResponseTime: number;
    workloadIndex: number;
  };
  trends?: {
    emailTrend: 'up' | 'down' | 'stable';
    correspondenceTrend: 'up' | 'down' | 'stable';
    responseTrend: 'up' | 'down' | 'stable';
  };
  healthStatus?: {
    overall: 'good' | 'warning' | 'critical';
    issues: string[];
    recommendations: string[];
  };
}

/**
 * Dashboard widget data
 */
interface DashboardWidgetData {
  metrics: {
    primary: Array<{
      label: string;
      value: number | string;
      icon: string;
      color: string;
      description: string;
      trend?: {
        direction: 'up' | 'down' | 'stable';
        percentage: number;
        period: string;
      };
    }>;
    secondary: Array<{
      label: string;
      value: number | string;
      description: string;
    }>;
  };
  activity: DashboardActivity[];
  tasks: DashboardTask[];
  alerts: Array<{
    type: 'info' | 'warning' | 'error';
    message: string;
    action?: string;
  }>;
}

/**
 * Dashboard analytics request
 */
interface DashboardAnalyticsRequest {
  period?: 'day' | 'week' | 'month' | 'quarter' | 'year';
  includeComparisons?: boolean;
  includeTrends?: boolean;
  includeBreakdowns?: boolean;
  timezone?: string;
}

/**
 * System-wide dashboard metrics (for implementors)
 */
interface SystemDashboardMetrics {
  organizations: {
    total: number;
    active: number;
    inactive: number;
    newThisMonth: number;
  };
  users: {
    total: number;
    active: number;
    newThisMonth: number;
    byRole: Record<UserRole, number>;
  };
  systemHealth: {
    status: 'healthy' | 'warning' | 'critical';
    uptime: number;
    performance: {
      responseTime: number;
      memoryUsage: number;
      cpuUsage: number;
    };
    storage: {
      used: number;
      total: number;
      percentage: number;
    };
  };
  usage: {
    totalEmails: number;
    totalCorrespondences: number;
    totalResponses: number;
    dailyActiveUsers: number;
  };
}

// ===== MAIN DASHBOARD QUERY HOOKS =====

/**
 * Main dashboard summary hook - FIXED to prevent infinite loops
 */
export const useDashboard = (options?: UseApiOptions) => {
  return useApi(
    async (): Promise<EnhancedDashboardSummary> => {
      
      const response = await apiClient.dashboard.getSummary();
      
      const dashboardData = normalizeSingleResponse<DashboardSummary>(response);
      
      // Enhance the dashboard data with calculated fields
      const enhancedData: EnhancedDashboardSummary = {
        ...dashboardData,
        calculatedMetrics: calculateMetrics(dashboardData),
        trends: calculateTrends(dashboardData),
        healthStatus: calculateHealthStatus(dashboardData)
      };
      
      return enhancedData;
    },
    {
      immediate: true,
      rateLimitKey: 'dashboard_summary',
      retryCount: 1,
      retryDelay: 2000,
      ...options
    }
  );
};

/**
 * Actionable dashboard summary (what needs the user's attention).
 */
export const useDashboardActionSummary = (options?: UseApiOptions) => {
  return useApi(
    async (): Promise<any> => {
      const response = await apiClient.dashboard.getActionSummary();
      return normalizeSingleResponse<any>(response);
    },
    {
      immediate: true,
      rateLimitKey: 'dashboard_action_summary',
      retryCount: 1,
      retryDelay: 2000,
      ...options
    }
  );
};

/**
 * Dashboard metrics hook - FIXED to prevent infinite loops
 */
export const useDashboardMetrics = (params?: DashboardQueryParams, options?: UseApiOptions) => {
  // FIXED: Create stable dependency array
  const dependencies = useMemo(() => {
    if (!params) return [];
    return [JSON.stringify(params)];
  }, [params]);

  return useApi(
    async (): Promise<DashboardMetrics> => {
      if (!params) {
        throw new Error('Dashboard metrics require parameters');
      }

      
      const response = await apiClient.dashboard.getMetrics(params);
      
      return normalizeSingleResponse<DashboardMetrics>(response);
    },
    {
      immediate: Boolean(params),
      dependencies,
      retryCount: 1,
      retryDelay: 2000,
      rateLimitKey: 'dashboard_metrics',
      ...options
    }
  );
};
/**
 * Recent activity hook - FIXED to prevent infinite loops
 */
export const useDashboardActivity = (params?: QueryParams, options?: UseApiOptions) => {
  // FIXED: Create stable dependency array
  const dependencies = useMemo(() => {
    if (!params) return [];
    return [JSON.stringify(params)];
  }, [params]);

  return useApi(
    async () => {
      
      // Get activity from dashboard summary to avoid separate API call
      const response = await apiClient.dashboard.getSummary();
      
      const dashboardData = normalizeSingleResponse<DashboardSummary>(response);
      
      // Transform to paginated response format
      const activities = dashboardData.recentActivity || [];
      const limitedActivities = params?.limit ? activities.slice(0, params.limit) : activities;
      
      return {
        data: limitedActivities,
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: activities.length,
          itemsPerPage: limitedActivities.length,
          hasNextPage: false,
          hasPrevPage: false
        },
        total: activities.length,
        totalPages: 1,
        page: 1,
        limit: limitedActivities.length
      };
    },
    {
      immediate: true,
      dependencies,
      retryCount: 1,
      retryDelay: 2000,
      rateLimitKey: 'dashboard_activity',
      ...options
    }
  );
};


/**
 * Upcoming tasks hook
 */
export const useDashboardTasks = (params?: QueryParams, options?: UseApiOptions) => {
  // FIXED: Create stable dependency array
  const dependencies = useMemo(() => {
    if (!params) return [];
    return [JSON.stringify(params)];
  }, [params]);

  return useApi(
    async (): Promise<PaginatedResponse<DashboardTask>> => {
      
      const response = await apiClient.dashboard.getSummary();
      
      const dashboardData = normalizeSingleResponse<DashboardSummary>(response);
      
      // Transform to paginated response
      const tasks = dashboardData.upcomingTasks || [];
      return {
        data: tasks,
        pagination: {
          currentPage: 1,
          totalPages: 1,
          totalItems: tasks.length,
          itemsPerPage: tasks.length,
          hasNextPage: false,
          hasPrevPage: false
        },
        total: tasks.length,
        totalPages: 1,
        page: 1,
        limit: tasks.length
      };
    },
    {
      immediate: true,
      dependencies,
      rateLimitKey: 'dashboard_tasks',
      retryCount: 1,
      ...options
    }
  );
};

/**
 * Dashboard preferences hook
 */
export const useDashboardPreferences = (options?: UseApiOptions) => {
  const memoizedOptions = useMemo(() => ({
    immediate: true,
    rateLimitKey: 'dashboard_preferences',
    retryCount: 1,
    ...options
  }), [options]);

  return useApi(
    async (): Promise<DashboardPreferences> => {
      
      // For now, we'll check if there's a preferences endpoint
      try {
        const response = await (apiClient.dashboard as any).getPreferences();
        return normalizeSingleResponse<DashboardPreferences>(response);
      } catch (error) {
        // If no preferences endpoint, return defaults
        return {
          dashboardRefreshInterval: 300,
          defaultView: 'grid',
          showCompletedActions: false,
          actionFilters: true,
          theme: 'light',
          autoRefresh: true,
          widgetConfiguration: {
            showMetrics: true,
            showActivity: true,
            showTasks: true,
            showNotifications: true,
            showStorage: true
          },
          compactMode: false,
          timeFormat: '24h',
          dateFormat: 'yyyy-MM-dd'
        };
      }
    },
    memoizedOptions
  );
};

/**
 * System dashboard metrics (for implementors)
 */
export const useSystemDashboardMetrics = (options?: UseApiOptions) => {
  const { user } = useAuthStore();
  
  // FIXED: Create stable dependencies based only on user role
  const dependencies = useMemo(() => {
    return [user?.role || ''];
  }, [user?.role]);

  return useApi(
    async (): Promise<SystemDashboardMetrics> => {
      if (user?.role !== 'implementor') {
        throw new Error('Access denied: System metrics available only to implementors');
      }
      
      
      // FIXED: Use the existing /api/organizations/implementor-data endpoint
      try {
        const response = await apiClient.organizations.getImplementorData();
        
        // Transform ImplementorDataResponse to SystemDashboardMetrics format
        const implementorData = normalizeSingleResponse<ImplementorDataResponse>(response);
        
        return {
          organizations: {
            total: implementorData.systemStats.totalOrganizations,
            active: implementorData.organizations.filter((org: any) => org.status === 'active').length,
            inactive: implementorData.organizations.filter((org: any) => org.status === 'deactivated').length,
            newThisMonth: 0 // Could be calculated if needed
          },
          users: {
            total: implementorData.systemStats.totalUsers,
            active: implementorData.systemStats.totalUsers, // Assuming all are active for now
            newThisMonth: 0, // Could be calculated if needed
            byRole: {
              user: 0,
              contributor: 0, 
              admin: 0,
              implementor: 1
            } // Would need role breakdown from backend
          },
          systemHealth: {
            status: implementorData.systemStats.systemHealth === 'degraded' ? 'warning' : implementorData.systemStats.systemHealth,
            uptime: 99.9, // Default value
            performance: {
              responseTime: 200,
              memoryUsage: 45,
              cpuUsage: 30
            },
            storage: {
              used: implementorData.systemStats.totalStorage,
              total: implementorData.systemStats.totalStorage * 1.2, // Estimate
              percentage: 83.3 // Estimate
            }
          },
          usage: {
            totalEmails: implementorData.organizations.reduce((total: number, org: any) => total + (org.storageUsed || 0), 0),
            totalCorrespondences: 0, // Would need from backend
            totalResponses: 0, // Would need from backend
            dailyActiveUsers: implementorData.organizations.reduce((total: number, org: any) => total + org.userCount, 0)
          }
        };
      } catch (error) {
        console.error('useSystemDashboardMetrics: Failed to fetch implementor data:', error);
        // Fallback to empty metrics
        return {
          organizations: { total: 0, active: 0, inactive: 0, newThisMonth: 0 },
          users: { total: 0, active: 0, newThisMonth: 0, byRole: {} as any },
          systemHealth: {
            status: 'healthy',
            uptime: 99.9,
            performance: { responseTime: 200, memoryUsage: 45, cpuUsage: 30 },
            storage: { used: 0, total: 0, percentage: 0 }
          },
          usage: { totalEmails: 0, totalCorrespondences: 0, totalResponses: 0, dailyActiveUsers: 0 }
        };
      }
    },
    {
      immediate: user?.role === 'implementor',
      dependencies,
      rateLimitKey: 'system_dashboard_metrics',
      retryCount: 1,
      ...options
    }
  );
};

// ===== DASHBOARD ANALYTICS HOOKS =====

/**
 * Dashboard analytics with custom parameters
 */
export const useDashboardAnalytics = (analyticsRequest?: DashboardAnalyticsRequest, options?: UseApiOptions) => {
  const stableDependencies = useMemo(() => 
    analyticsRequest ? [JSON.stringify(analyticsRequest)] : [], 
    [analyticsRequest]
  );

  const memoizedOptions = useMemo(() => ({
    immediate: true,
    dependencies: stableDependencies,
    rateLimitKey: 'dashboard_analytics',
    retryCount: 1,
    ...options
  }), [stableDependencies, options]);

  return useApi(
    async () => {
      
      const params: DashboardQueryParams = {
        period: analyticsRequest?.period || 'week',
        includeDetails: true,
        timezone: analyticsRequest?.timezone
      };
      
      const response = await apiClient.dashboard.getMetrics(params);
      
      return normalizeSingleResponse(response);
    },
    memoizedOptions
  );
};

/**
 * Dashboard widget configuration hook
 */
export const useDashboardWidgets = (preferences?: DashboardPreferences, options?: UseApiOptions) => {
  const stableDependencies = useMemo(() => 
    preferences ? [JSON.stringify(preferences)] : [], 
    [preferences]
  );

  const memoizedOptions = useMemo(() => ({
    immediate: true,
    dependencies: stableDependencies,
    rateLimitKey: 'dashboard_widgets',
    retryCount: 1,
    ...options
  }), [stableDependencies, options]);

  return useApi(
    async (): Promise<DashboardWidgetData> => {
      
      const [summaryResponse, metricsResponse] = await Promise.all([
        apiClient.dashboard.getSummary(),
        apiClient.dashboard.getMetrics({ period: 'week' })
      ]);
      
      const summary = normalizeSingleResponse<DashboardSummary>(summaryResponse);
      const metrics = normalizeSingleResponse<DashboardMetrics>(metricsResponse);
      
      const widgetData: DashboardWidgetData = {
        metrics: buildMetricsWidgets(summary, metrics),
        activity: summary.recentActivity || [],
        tasks: summary.upcomingTasks || [],
        alerts: buildAlerts(summary, metrics)
      };
      
      return widgetData;
    },
    memoizedOptions
  );
};

/**
 * Dashboard health monitoring hook
 */
export const useDashboardHealth = (options?: UseApiOptions) => {
  const memoizedOptions = useMemo(() => ({
    immediate: true,
    rateLimitKey: 'dashboard_health',
    retryCount: 1,
    ...options
  }), [options]);

  return useApi(
    async () => {
      
      try {
        const response = await (apiClient as any).system?.health();
        return response;
      } catch (error) {
        return {
          status: 'unknown',
          timestamp: new Date().toISOString(),
          message: 'Health check not available'
        };
      }
    },
    memoizedOptions
  );
};

// ===== DASHBOARD MUTATION HOOKS =====

/**
 * Update dashboard preferences
 */
export const useUpdateDashboardPreferences = (options?: UseMutationOptions<DashboardPreferences, DashboardPreferencesUpdateRequest>) => {
  return useMutation(
    async (preferences: DashboardPreferencesUpdateRequest): Promise<DashboardPreferences> => {
      
      // Check if update endpoint exists
      try {
        const response = await (apiClient.dashboard as any).updatePreferences(preferences);
        return normalizeSingleResponse<DashboardPreferences>(response);
      } catch (error) {
        // If no preferences endpoint, store locally (temporary solution)
        const currentPrefs = JSON.parse(localStorage.getItem('dashboardPreferences') || '{}');
        const updatedPrefs = { ...currentPrefs, ...preferences };
        localStorage.setItem('dashboardPreferences', JSON.stringify(updatedPrefs));
        return updatedPrefs as DashboardPreferences;
      }
    },
    options
  );
};

/**
 * Refresh dashboard data
 */
export const useRefreshDashboard = (options?: UseMutationOptions<EnhancedDashboardSummary, void>) => {
  return useMutation(
    async (): Promise<EnhancedDashboardSummary> => {
      
      const response = await apiClient.dashboard.getSummary();
      
      const dashboardData = normalizeSingleResponse<DashboardSummary>(response);
      
      const enhancedData: EnhancedDashboardSummary = {
        ...dashboardData,
        calculatedMetrics: calculateMetrics(dashboardData),
        trends: calculateTrends(dashboardData),
        healthStatus: calculateHealthStatus(dashboardData)
      };
      
      return enhancedData;
    },
    options
  );
};

// ===== UTILITY FUNCTIONS =====

/**
 * Calculate enhanced metrics from dashboard data
 */
function calculateMetrics(data: DashboardSummary) {
  const counts = data.counts || {};
  
  return {
    productivityScore: Math.min(100, (counts.responses || 0) * 10 + (counts.correspondences || 0) * 5),
    responseRate: counts.correspondences ? 
      Math.round(((counts.responses || 0) / counts.correspondences) * 100) : 0,
    averageResponseTime: 2.5, // hours - would come from analytics
    workloadIndex: Math.min(100, (counts.pendingApprovals || 0) * 20)
  };
}

/**
 * Calculate trends from dashboard data
 */
function calculateTrends(data: DashboardSummary) {
  // This would typically compare with historical data
  return {
    emailTrend: 'stable' as const,
    correspondenceTrend: 'up' as const,
    responseTrend: 'stable' as const
  };
}

/**
 * Calculate health status from dashboard data
 */
function calculateHealthStatus(data: DashboardSummary) {
  const counts = data.counts || {};
  const issues: string[] = [];
  const recommendations: string[] = [];
  
  if ((counts.pendingApprovals || 0) > 10) {
    issues.push('High number of pending approvals');
    recommendations.push('Review and process pending items');
  }
  
  if ((counts.responses || 0) === 0 && (counts.correspondences || 0) > 0) {
    issues.push('No responses created for active correspondences');
    recommendations.push('Create responses for pending correspondences');
  }
  
  return {
    overall: issues.length === 0 ? 'good' as const : 
             issues.length <= 2 ? 'warning' as const : 'critical' as const,
    issues,
    recommendations
  };
}

/**
 * Build metrics widgets from dashboard data
 */
function buildMetricsWidgets(summary: DashboardSummary, metrics: DashboardMetrics): DashboardWidgetData['metrics'] {
  const counts = summary.counts || {};
  
  return {
    primary: [
      {
        label: 'Total Emails',
        value: counts.emails || 0,
        icon: '📧',
        color: 'text-blue-600',
        description: 'All emails in system'
      },
      {
        label: 'Active Correspondences',
        value: counts.correspondences || 0,
        icon: '💬',
        color: 'text-green-600',
        description: 'Currently active correspondences'
      },
      {
        label: 'Responses',
        value: counts.responses || 0,
        icon: '✍️',
        color: 'text-purple-600',
        description: 'Total responses created'
      },
      {
        label: 'Projects',
        value: counts.projects || 0,
        icon: '📋',
        color: 'text-orange-600',
        description: 'Active projects'
      }
    ],
    secondary: [
      {
        label: 'Pending Approvals',
        value: counts.pendingApprovals || 0,
        description: 'Items awaiting approval'
      },
      {
        label: 'Storage Used',
        value: summary.storageUsage?.percentage || 0,
        description: 'Storage utilization'
      }
    ]
  };
}

/**
 * Build alerts from dashboard data
 */
function buildAlerts(summary: DashboardSummary, metrics: DashboardMetrics): DashboardWidgetData['alerts'] {
  const alerts = [];
  const counts = summary.counts || {};
  
  if ((counts.pendingApprovals || 0) > 5) {
    alerts.push({
      type: 'warning' as const,
      message: `You have ${counts.pendingApprovals} items pending approval`,
      action: 'Review pending items'
    });
  }
  
  if ((summary.storageUsage?.percentage || 0) > 80) {
    alerts.push({
      type: 'warning' as const,
      message: 'Storage usage is above 80%',
      action: 'Archive old files'
    });
  }
  
  return alerts;
}